import {
  BoxType,
  preserveClippingSourceMasks,
  clippingSourceMaskGuard,
  type CustomTemplate,
} from '@lite-workspace/dto-fotoclass-templates';
import type { CustomTemplateBox } from '@lite-workspace/dto-fotoclass-templates';
import type { BookCoverData, BookData } from '../interfaces/book-data.interface';
import type { CollageData } from '../interfaces/collage-data.interface';
import type { SpreadData } from '../interfaces/spread-data.interface';
import type { DrawableData } from '../interfaces/drawable-data.interface';
import { forEachDrawableDeep, mapDrawablesDeep, mapSpreadDrawablesDeep } from '../queries/drawable-tree';
import { createNormalizerContext } from './boxes/codec-contract';
import type { NormalizerContext } from './boxes/codec-contract';
import { decode as decodeImage, decodeFrameSibling } from './boxes/image-box';
import { decode as decodeText } from './boxes/text-box';
import { decode as decodeObject } from './boxes/object-box';
import { decode as decodeSmartObject } from './boxes/smart-object-box';
import { decode as decodeBackground } from './boxes/background-box';
import { decode as decodeGroup } from './boxes/group-box';
import { decode as decodeVector } from './boxes/vector-box';
import { decode as decodeAdjustment } from './boxes/adjustment-box';
import { decode as decodeUnsupported } from './boxes/unsupported-box';
import { decodeSpreadBackground } from './spread-background';
import { buildDrawableEffects } from './boxes/effects';
import { detectSchemaVersion } from './schema-detect';
import type { NormalizeIssue, PersistedBookModelV1, PersistedCollageV1, PersistedSpreadV1 } from './types';

/**
 * Dispatch by box.type. `box` is typed `CustomTemplateBox` (the 8-variant closed union) but arrives
 * from `unknown` persisted JSON — a genuinely unfamiliar `type` string at runtime still falls through
 * to `default`, routing to the unsupported-box backstop instead of throwing or vanishing (§3 #19).
 */
function decodeTemplateBox(box: CustomTemplateBox, ctx: NormalizerContext): DrawableData {
  switch (box.type) {
    case BoxType.IMAGE:
      return decodeImage(box, ctx);
    case BoxType.TEXT:
      return decodeText(box, ctx);
    case BoxType.OBJECT:
      return decodeObject(box, ctx);
    case BoxType.BACKGROUND:
      return decodeBackground(box, ctx);
    case BoxType.SMART_OBJECT:
      return decodeSmartObject(box, ctx);
    case BoxType.GROUP:
      return decodeGroup(box, ctx);
    case BoxType.VECTOR:
      return decodeVector(box, ctx);
    case BoxType.ADJUSTMENT:
      return decodeAdjustment(box, ctx);
    default:
      return decodeUnsupported(box, ctx);
  }
}

/**
 * §3 #12 — an image box carrying frameUrl/frameFileId/frameWindow expands into TWO drawables (the
 * image + a `${id}__frame` object sibling immediately after it), matching `generateDrawableImageFrame`.
 * The `order` field is a dispatcher concern (every box codec leaves it 0) — assigned here by final
 * array position, after any frame expansion.
 */
function decodeCollageBoxes(boxes: readonly CustomTemplateBox[], ctx: NormalizerContext): readonly DrawableData[] {
  const flat: DrawableData[] = [];
  boxes.forEach((box, index) => {
    const boxCtx = ctx.child(index);
    if (box.type === BoxType.IMAGE) {
      const image = decodeImage(box, boxCtx);
      flat.push(image);
      const frame = decodeFrameSibling(box, image);
      if (frame) flat.push(frame);
      return;
    }

    flat.push(decodeTemplateBox(box, boxCtx));
  });

  return flat.map((drawable, order) => ({ ...drawable, order }));
}

function decodeCollage(collage: PersistedCollageV1, ctx: NormalizerContext): CollageData {
  const { boxes, ...rest } = collage;
  // A mixed document is decoded by the FIRST collage's shape (schema-detect), so a legacy collage can
  // arrive here with no `boxes` — it is already in target shape and passes through untouched.
  if (!Array.isArray(boxes)) {
    return collage as unknown as CollageData;
  }

  const template = collage.originalCustomTemplate as Partial<CustomTemplate> | null;
  return {
    ...rest,
    drawableObjects: decodeCollageBoxes(preserveClippingSourceMasks(boxes, template?.originalLayerTree), ctx.child('boxes')),
  };
}

function decodeSpreadCommon(spread: PersistedSpreadV1, ctx: NormalizerContext): SpreadData {
  const { collages, background, ...rest } = spread;
  const collagesCtx = ctx.child('collages');
  return {
    ...rest,
    collages: collages.map((collage, i) => decodeCollage(collage, collagesCtx.child(i))),
    background: decodeSpreadBackground(background, ctx.child('background')),
  };
}

function decodeCover(cover: PersistedBookModelV1['cover'], ctx: NormalizerContext): BookCoverData {
  return { ...decodeSpreadCommon(cover, ctx), type: cover.type };
}

function createDecodeRootContext(onIssue: (issue: NormalizeIssue) => void): NormalizerContext {
  const ctx: NormalizerContext = createNormalizerContext({
    onIssue,
    decodeBox: box => decodeTemplateBox(box, ctx.child(box.id)),
    // Shared with the top-level collage decode — a group's own `boxes` gets the same
    // frame-sibling expansion + positional `order` renumbering, not the single-box dispatch.
    decodeBoxList: boxes => decodeCollageBoxes(boxes, ctx),
  });
  return ctx;
}

/**
 * Spread-level v1→v0 — the seam a v1-native store crosses to run a legacy-typed helper on one
 * spread. Same codec as `denormalize`, scoped to one spread; no schema detection (input is v1).
 */
export function denormalizeSpread(spread: PersistedSpreadV1): { model: SpreadData; issues: readonly NormalizeIssue[] } {
  const issues: NormalizeIssue[] = [];
  const rootCtx = createDecodeRootContext(issue => issues.push(issue));
  return { model: decodeSpreadCommon(spread, rootCtx.child('spread')), issues };
}

export function denormalize(raw: unknown): { model: BookData; issues: readonly NormalizeIssue[] } {
  const issues: NormalizeIssue[] = [];
  const onIssue = (issue: NormalizeIssue): number => issues.push(issue);
  const detected = detectSchemaVersion(raw);
  detected.issues.forEach(onIssue);

  if (detected.schemaVersion === 'legacy-drawable-v0') {
    // Promote old raw-only bevels before font changes invalidate the import metadata.
    const legacy = raw as BookData;
    const promoteBevel = (drawable: DrawableData): DrawableData => {
      const meta = drawable.templateMeta;
      const originalEffects = meta?.rawEffects;
      if (
        !originalEffects ||
        !(originalEffects.bevel || originalEffects.satin || originalEffects.patternOverlay || originalEffects.glows)
      )
        return drawable;
      const { bevel, satin, patternOverlay, glows, ...rawEffects } = originalEffects;
      const effects = [...(drawable.effects ?? buildDrawableEffects({ glows }) ?? [])];
      for (const effect of buildDrawableEffects({ bevel, satin, patternOverlay }) ?? []) {
        if (!effects.some(current => current.kind === effect.kind)) effects.push(effect);
      }

      const typedGlows = effects.filter(effect => effect.kind === 'glow');
      let glowIndex = 0;
      const promoted = effects.map(effect =>
        effect.kind === 'glow' && glows?.length === typedGlows.length ? { ...glows[glowIndex++], ...effect } : effect,
      );
      return {
        ...drawable,
        effects: promoted,
        templateMeta: { ...meta, rawEffects: Object.keys(rawEffects).length ? rawEffects : undefined },
      } as DrawableData;
    };

    const upgrade = <T extends SpreadData>(spread: T): T => {
      const withBevel = mapSpreadDrawablesDeep(spread, promoteBevel);
      const collages = (withBevel.collages ?? []).map(collage => {
        const sources: { id: string; idFromTemplate?: string; psdLayerId?: string }[] = [];
        forEachDrawableDeep(collage.drawableObjects ?? [], drawable =>
          sources.push({
            id: drawable.id,
            idFromTemplate: drawable.idFromTemplate,
            psdLayerId: drawable.templateMeta?.psdLayerId,
          }),
        );
        const template = collage.originalCustomTemplate as Partial<CustomTemplate> | null;
        const requiresStatic = clippingSourceMaskGuard(sources, template?.originalLayerTree);
        const drawableObjects = mapDrawablesDeep(collage.drawableObjects ?? [], drawable => {
          const recipe = drawable.templateMeta?.compoundClipBakeRecipe;
          if (!recipe || recipe.preserveStaticMask || !recipe.leafBoxIds.some(requiresStatic)) return drawable;
          return {
            ...drawable,
            templateMeta: { ...drawable.templateMeta, compoundClipBakeRecipe: { ...recipe, preserveStaticMask: true } },
          } as DrawableData;
        });
        return drawableObjects === collage.drawableObjects ? collage : { ...collage, drawableObjects };
      });
      return collages.some((collage, i) => collage !== withBevel.collages[i]) ? { ...withBevel, collages } : withBevel;
    };

    const cover = legacy.cover ? upgrade(legacy.cover) : legacy.cover;
    const spreads = (legacy.spreads ?? []).map(upgrade);
    const changed = cover !== legacy.cover || spreads.some((spread, i) => spread !== legacy.spreads[i]);
    return { model: changed ? { ...legacy, cover, spreads } : legacy, issues };
  }

  const rootCtx = createDecodeRootContext(onIssue);
  const doc = raw as PersistedBookModelV1;
  const { cover, spreads, schemaVersion: _schemaVersion, ...rest } = doc;
  const spreadsCtx = rootCtx.child('spreads');
  const model: BookData = {
    ...rest,
    cover: decodeCover(cover, rootCtx.child('cover')),
    spreads: spreads.map((spread, i) => decodeSpreadCommon(spread, spreadsCtx.child(i))),
  };

  return { model, issues };
}

export function denormalizeAndLog(raw: unknown, log: (issue: NormalizeIssue) => void): BookData {
  const { model, issues } = denormalize(raw);
  issues.forEach(log);
  return model;
}
